import { useState } from 'react';
import { debounce } from '../../../utils/debounce';
import styles from './Table.module.scss';

type Props = {
  /**
   * The content to render within the component.
   * Typically `TableHead`, `TableBody`, and `TableFoot` components.
   */
  children: React.ReactNode;
  /**
   * Whether to collapse the borders of the table cells.
   */
  collapse?: boolean;
  /**
   * The kind of table to render. Controls its visual styles.
   * When a non-`custom` `kind` is supplied, `Table` will style
   * itself and its subcomponents (e.g. `td`, `th`, `tr` nodes).
   * When a `custom` `kind` is supplied, consumers are responsible
   * for almost all styling.
   */
  kind?: 'standard' | 'custom';
  /**
   * Whether `Table` can be scrolled.
   */
  scrollable?: boolean;
};

/**
 * Highly flexible `Table` component for displaying data.
 */
export const Table = ({
  scrollable,
  children,
  collapse = false,
  kind = 'standard',
}: Props) => {
  const [isScrolledHorz, setIsScrolledHorz] = useState(false);
  const [isScrolledVert, setIsScrolledVert] = useState(false);
  // Debounce to prevent state thrashing. Separate debounced
  // function from `onScroll` to work around event pooling
  // https://reactjs.org/docs/legacy-event-pooling.html
  const setScroll = debounce((target: HTMLDivElement) => {
    const { scrollLeft, scrollTop } = target;
    setIsScrolledHorz(scrollLeft > 0);
    setIsScrolledVert(scrollTop > 0);
  }, 20);
  const onScroll = (e: React.SyntheticEvent) => {
    setScroll(e.target);
  };
  return (
    <div
      className={styles.tableWrapper}
      data-scrollable={scrollable ? true : undefined}
      data-scrolled-horz={isScrolledHorz ? true : undefined}
      data-scrolled-vert={isScrolledVert ? true : undefined}
      data-zds
      onScroll={onScroll}
    >
      <table
        className={styles.table}
        data-collapse={collapse ? true : undefined}
        data-kind={kind}
        data-zds
        role="table"
      >
        {children}
      </table>
    </div>
  );
};
